import type { ReactElement } from 'react';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupIcon from '@mui/icons-material/Group';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import type { ModuleDefinition } from '../../app/moduleRegistry';
import { gsaModule } from './gsa';
import { userModule } from './user';
import { credentialModule } from './credential';
import { bankModule } from './bank';

/**
 * SYSTEM ADMIN screens (Gsa / User / Credential / Bank) — migration guide Bölüm 3.7, 3.10,
 * 5.3 ("Sistem" section). Master-GSA-only: each sub-module's own index.tsx already gates its
 * menu item behind `isVisible: (user) => !!user?.isMasterGsa`. One combined module aggregating
 * the 4 independent sub-modules' routes/menuItems so app/modules.ts only ever imports a single
 * `systemModule`, matching every other multi-sub-module aggregator in this codebase (see
 * modules/corporate/index.tsx for the same pattern). Icons attached here rather than
 * per-sub-module so the sub-modules stay icon-agnostic.
 *
 * Route/shape confirmation — see api/types/system.ts / api/endpoints/system.ts headers for
 * the authoritative per-route detail. Summary:
 * - Gsa: full CRUD confirmed (AddGsa + GetGsaList + EditGsa + DeleteGsa, swagger 2026-08-27).
 *   Only `Gsa/UploadLogo` is absent (best-effort multipart, falls back to EditGsa.logo).
 * - User: fully confirmed end-to-end (GetUsers 200s with real rows).
 * - Credential / Bank: re-verified 2026-08-10 against a local backend (localhost:1538) — the
 *   controllers DO exist there (GetCredentials / GetBanks returned real rows), Edit-only
 *   (legacy web tier had no Create/Delete either). They were absent from the shared test host
 *   testbookingapiv2.arkman.com.tr — confirm on the production host (docs/backend-requirements.md V-2).
 */
const withIcons: Record<string, ReactElement> = {
  'system/gsa': <ApartmentIcon />,
  'system/user': <GroupIcon />,
  'system/credential': <VpnKeyIcon />,
  'system/bank': <AccountBalanceIcon />,
};

const subModules: ModuleDefinition[] = [gsaModule, userModule, credentialModule, bankModule];

export const systemModule: ModuleDefinition = {
  routes: subModules.flatMap((m) => m.routes),
  menuItems: subModules.flatMap((m, moduleIdx) =>
    m.menuItems.map((item, itemIdx) => ({
      ...item,
      label: item.label.replace(/^Sistem:\s*/, ''),
      icon: item.icon ?? withIcons[item.path],
      group: 'Sistem',
      groupIcon: moduleIdx === 0 && itemIdx === 0 ? <SettingsOutlinedIcon /> : undefined,
    }))
  ),
};
